import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import bottle from "../assets/bottleImg.png";

gsap.registerPlugin(ScrollTrigger);

const HeroSection = () => {
  const bottleRef = useRef(null);
  const bigTextRef = useRef(null);
  const section1Ref = useRef(null);
  const section2Ref = useRef(null);
  const textRightRef = useRef(null);

  useEffect(() => {
    const bottleEl = bottleRef.current;
    const bigText = bigTextRef.current;
    const section2 = section2Ref.current;
    const textRight = textRightRef.current;

    // ── Entrance animation: bottle slides up + fades in on load ──────────────
    gsap.fromTo(
      bottleEl,
      { y: 140, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 1.6,
        ease: "power3.out",
        delay: 0.3,
      },
    );

    // ── Big text fade in ──────────────────────────────────────────────────────
    gsap.fromTo(
      bigText,
      { opacity: 0 },
      { opacity: 1, duration: 2, ease: "power2.out", delay: 0.5 },
    );

    // ── Scroll: bottle moves from center to left (fixed, always visible) ──────
    // It starts centered (translateX 0), moves to left side as section2 enters
    gsap.to(bottleEl, {
      x: "-28vw",
      rotate: -42,
      scale: 0.78,
      ease: "power2.inOut",
      scrollTrigger: {
        trigger: section2,
        start: "top bottom", // when top of section2 hits bottom of viewport
        end: "top 30%", // when top of section2 reaches 30% from top
        scrub: 1.2,
      },
    });

    // ── Scroll: right text fades in ───────────────────────────────────────────
    gsap.fromTo(
      textRight,
      { opacity: 0, x: 60 },
      {
        opacity: 1,
        x: 0,
        ease: "power2.out",
        scrollTrigger: {
          trigger: section2,
          start: "top 50%",
          end: "top 10%",
          scrub: 1,
        },
      },
    );

    return () => {
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, []);

  return (
    <Wrapper>
      {/* ── Fixed bottle — always visible, animates left on scroll ── */}
      <BottleWrapper ref={bottleRef}>
        <BottleImg src={bottle} alt="Argo Drinks bottle" />
      </BottleWrapper>

      {/* ── SECTION 1: Hero ─────────────────────────────────────────── */}
      <Section1 ref={section1Ref}>
        <BigText ref={bigTextRef}>
          ARGO <br />
          DRINKS
        </BigText>

        <BottomRow>
          <Description>
            Crafted from the rarest vineyards,
            <br />
            Argo is bold by nature.
          </Description>
          <OrderBtn>Order Now</OrderBtn>
        </BottomRow>
      </Section1>

      {/* ── SECTION 2: Bottle left + text right ─────────────────────── */}
      <Section2 ref={section2Ref}>
        <TextRight ref={textRightRef}>
          <S2Headline>Tek ne gjeni pijet, për të shijuar momentet!</S2Headline>
          <S2Tagline>
            Every bottle of Argo carries centuries of tradition —<br />
            poured into a single, unforgettable experience.
          </S2Tagline>
          <S2Cta>Discover the Collection</S2Cta>
        </TextRight>
      </Section2>
    </Wrapper>
  );
};

export default HeroSection;

// ─── Styled Components ────────────────────────────────────────────────────────

const Wrapper = styled.div`
  position: relative;
`;

/* ── Fixed bottle — sits in viewport center, animated by GSAP ── */
const BottleWrapper = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(90deg);
  z-index: 10;
  width: 55%;
  opacity: 0;
  pointer-events: none;
`;

const BottleImg = styled.img`
  width: 100%;
  height: auto;
  object-fit: contain;
  opacity: 0.7;
  filter: drop-shadow(0 40px 80px rgba(0, 0, 0, 0.85));
  filter: blur(1px);
`;

/* ── Section 1 ── */
const Section1 = styled.section`
  position: relative;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const BigText = styled.h1`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-display);
  font-size: clamp(18vw, 22vw, 26vw);
  color: var(--accent);
  letter-spacing: -0.02em;
  line-height: 1;
  user-select: none;
  pointer-events: none;
  opacity: 0;
  z-index: 1;
  line-height: 0.75;
`;

const BottomRow = styled.div`
  position: absolute;
  bottom: 3rem;
  left: 3rem;
  right: 3rem;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  z-index: 3;
`;

const Description = styled.p`
  font-family: var(--font-body);
  font-size: 0.88rem;
  font-weight: 300;
  line-height: 1.7;
  color: var(--white-dim);
  max-width: 280px;
`;

const OrderBtn = styled.button`
  font-family: var(--font-condensed);
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--white);
  background: var(--accent);
  border: none;
  border-radius: 999px;
  padding: 0.9rem 2.2rem;
  cursor: pointer;
  transition:
    background 0.25s ease,
    transform 0.2s ease;

  &:hover {
    background: var(--accent-light);
    transform: scale(1.04);
  }
`;

/* ── Section 2 ── */
const Section2 = styled.section`
  position: relative;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 8vw;
  overflow: hidden;
`;

const TextRight = styled.div`
  max-width: 880px;
  opacity: 0;
  z-index: 3;
`;

const S2Headline = styled.h2`
  font-family: var(--font-display);
  font-size: clamp(5rem, 8vw, 8.5rem);
  line-height: 0.95;
  color: var(--white);
  margin-bottom: 1.5rem;
  letter-spacing: 0.02em;
`;

const S2Tagline = styled.p`
  font-family: var(--font-body);
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.8;
  color: var(--white-dim);
  margin-bottom: 2.5rem;
`;

const S2Cta = styled.button`
  font-family: var(--font-condensed);
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--accent);
  background: transparent;
  border: 1.5px solid var(--accent);
  border-radius: 999px;
  padding: 0.9rem 2.4rem;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    background: var(--accent);
    color: var(--white);
  }
`;
